import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import SpaIcon from "@mui/icons-material/Spa";
import ScienceIcon from "@mui/icons-material/Science";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import { Button, Icon, ICONS } from "abs-ui-components";
import "../VesselDetails.scss";
import { myFleetActions } from "../../../store/myFleet/myFleet.slice";
import axiosHttp from "../../../services/interceptor";
import AbsModal from "../../../common/absModal/AbsModal";
import FuelFormcontainer from "../FuelregistryV2/FuelFormContainer";
import FuelRegistryForm from "../FuelregistryV2/FuelRegistryForm";
import roundNumericValues from "../../../common/utils/RoundNumericValues";

const baseURL = process.env.REACT_APP_BASE_URL;

const getFuelRegistry = async (vesselMstId, fuelRegistryPayloadParsed) => {
    try {
        var url = vesselMstId && vesselMstId > 0 ? `${baseURL}api/FuelRegistry/vessel/${vesselMstId}` : `${baseURL}api/FuelRegistry/base`;

        const response = await axiosHttp.get(url);
        return getLatestFuelRegistry(roundNumericValues(response.data?.data || [], 3), fuelRegistryPayloadParsed) || [];
    } catch (error) {
        console.log("API ERROR:", error);
        return [];
    }
};

const setUniqueIds = (fuelRegRes = []) => {
    var mappedRes = fuelRegRes.map(item => {
        item = {
            ...item,
            _tempId: `${(item.fuelRegId ?? 0) > 0 ? item.fuelRegId : crypto.randomUUID()}`,
        };
        return item;
    });
    return mappedRes;
};

const getLatestFuelRegistry = (responseData, fuelRegistryPayloadParsed) => {
    const latest = setUniqueIds(responseData)?.map((x) => {
        x.isEdited = x.vesselMstId > 0;
        if (x.childFuels?.length > 0) {
            x.childFuels = x.childFuels.map((x) => ({ ...x, isEdited: x.vesselMstId > 0 }));
        }
        var updated = fuelRegistryPayloadParsed?.find((y) => y.categoryType === "Blend" && x._tempId !== x.fuelRegId
            ? x._tempId === y._tempId
            : (x.code === y.code && x.categoryType === y.categoryType));
        if (updated) {
            updated.isEdited = true;
            if (updated?.childFuels.length > 0) {
                updated.childFuels = updated.childFuels.map((x) => ({ ...x, isEdited: true }));
            }
            return { ...x, ...updated };
        }
        return x;
    }) ?? [];
    var result = latest.concat(fuelRegistryPayloadParsed.filter((y) => !responseData.some((x) => y.categoryType === "Blend"
        ? String(x.fuelRegId ?? x._tempId) === String(y.fuelRegId ?? y._tempId)
        : (x.code === y.code && x.categoryType === y.categoryType)))
        ?.map(x => {
            x.isEdited = true;
            if (x.childFuels?.length > 0) {
                x.childFuels = x.childFuels.map((c) => ({ ...c, isEdited: true }));
            }
            return x;
        })).filter(x => x.categoryType === "Fossil" || x.code === "ETHA" || x.fuelRegId < 0 || x.vesselMstId > 0 || x.isEdited) ?? [];

    const categoryOrder = {
        fossil: 1,
        biofuel: 2,
        "e-fuel": 3,
        blend: 4,
    };

    return result.sort((a, b) => {
        const aCategory = a.categoryType.toLowerCase();
        const bCategory = b.categoryType.toLowerCase();

        const aRank = categoryOrder[aCategory] ?? 999;
        const bRank = categoryOrder[bCategory] ?? 999;

        // First: sort by category
        if (aRank !== bRank) {
            return aRank - bRank;
        }

        if (aCategory === "blend" && bCategory === "blend") {
            return getBlendOrder(a.code) - getBlendOrder(b.code);
        }

        return 0;
    });
};

const getBlendOrder = (code) => {
    if (!code) return 999;
    const parts = String(code).split(".");
    return parts[1] ? Number(parts[1]) : 0;
};

const transformFuelData = (vesselMstId, FuelRegistries, isChild = false) => {

    return FuelRegistries.map((item) => {
        const metadata =
            item.metadata?.reduce((acc, meta) => {
                const key = meta.metaKey.replace(/\|/g, "");
                acc[key] = meta.metaValue;
                return acc;
            }, {}) || {};

        const metaTypedata =
            item.metadata?.reduce((acc, meta) => {
                const key = meta.metaKey.replace(/\|/g, "");
                acc[key] = {
                    value: meta.metaValue,
                    type: meta.metaType,
                    isOverwritten: meta.isOverWritten || false
                };
                return acc;
            }, {}) || {};

        let massPercent = metadata.PERCENTMASS || null;
        if (item.categoryType === "COMPONENT") {
            massPercent = item.mass || metadata.PERCENTMASS || null;
        }

        const normalizeCslip = (value) => {
            if (
                value === true ||
                value === "TRUE" ||
                value === 1 ||
                value === "1"
            ) {
                return "1";
            }

            if (
                value === false ||
                value === "FALSE" ||
                value === 0 ||
                value === "0"
            ) {
                return "0";
            }

            return null;
        };

        let cslipVal = normalizeCslip(metadata.CSLIP);
        let ukCslipVal = normalizeCslip(metadata.UKCSLIP);

        let transformedItem = {
            _tempId: item._tempId,
            IsEdited: item.isEdited,
            FuelCode: item.code,
            FuelType: item.name,
            FuelName: item.name,
            FuelCategory: item.categoryType === "BLEND" ? "Blend" : item.categoryType,
            IsEditable: item.categoryType !== "Fossil" && item.categoryType !== "BLEND",
            FuelRegId: item.fuelRegId,
            ParentFuelRegId: item.parentFuelRegId,
            ...metadata,
            BDNPoS: metadata.BDNID || null,
            Quantity: metadata.QUANTITYMT || null,
            EnergyContent: metadata.ENERGYCONTENTMJ || null,
            EValue: metadata.EVALUEGCO2EQMJ || null,
            MassPercent: massPercent,
            EnergyPercent: metadata.PERCENTENERGY || null,
            IMOLCV: metadata.IMOLCV || null,
            EULCV: metadata.EULCV || null,
            EUVALUE: metadata.EUVALUE || null,
            CO2eqWtT: metadata.CO2EQWTT || null,
            IMOCFCO2: metadata.IMOCFCO2 || null,
            EUMRVEFCO2: metadata.EUMRVEFCO2 || null,
            EUMRVEFCH4: metadata.EUMRVEFCH4 || null,
            EUMRVEFN2O: metadata.EUMRVEFN2O || null,
            EUETSEFCO2: metadata.EUETSEFCO2 || null,
            EUETSEFCH4: metadata.EUETSEFCH4 || null,
            EUETSEFN2O: metadata.EUETSEFN2O || null,
            FEUEFCO2: metadata.FEUEFCO2 || null,
            FEUEFCH4: metadata.FEUEFCH4 || null,
            FEUEFN2O: metadata.FEUEFN2O || null,
            UKMRVEFCO2: metadata.UKMRVEFCO2 || null,
            RWD: metadata.RWD || null,
            Cslip: cslipVal === "1" ? "Applicable" : cslipVal === "0" ? "Not Applicable" : null,
            UKETSEFCO2: metadata.UKETSEFCO2 || null,
            UKETSEFCH4: metadata.UKETSEFCH4 || null,
            UKETSEFN2O: metadata.UKETSEFN2O || null,
            UKCslip: ukCslipVal === "1" ? "Applicable" : ukCslipVal === "0" ? "Not Applicable" : null,
            MetaTypeMap: metaTypedata,
        };
        const metaOverwriteMap = item.metadata?.reduce((acc, meta) => {
            acc[meta.metaKey.replace(/\|/g, "")] = meta.isOverWritten || false;
            return acc;
        }, {});
        transformedItem.MetaOverwriteMap = metaOverwriteMap;


        if (item.childFuels && item.childFuels.length > 0) {
            transformedItem.subRows = transformFuelData(vesselMstId, item.childFuels, true);
            transformedItem.IsEditable = true;
        } else {
            if (vesselMstId > 0) {
                transformedItem.IsEditable = ((item.categoryType !== "Fossil") || item.isEdited) && !isChild;
            } else {
                transformedItem.IsEditable = ((item.categoryType !== "Fossil" && (item.categoryType !== "Biofuel" && item.FuelCode !== "ETHA")) || item.isEdited) && !isChild;
            }

        }

        return transformedItem;
    });
};


const BlendFuelRegistry = (props) => {
    const dispatch = useDispatch();
    const [fuelData, setFuelData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFuel, setSelectedFuel] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const fuelRegistryPayloadParsed = useSelector((state) =>
        state.myFleet.fuelRegistryPayload
            ? Array.isArray(state.myFleet.fuelRegistryPayload)
                ? state.myFleet.fuelRegistryPayload
                : JSON.parse(state.myFleet.fuelRegistryPayload)
            : []
    );
    const openModal = () => {
        setSelectedFuel(null);
        setIsModalOpen(true);
    };

    const openEditModal = (row) => {
        setSelectedFuel(row.original);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedFuel(null);

        if (table.refetch) {
            table.refetch();
        }
    };
    // useEffect(() => {
    //     if (!isModalOpen) return;
    //     const handleBackdropClick = (event) => {
    //         if (event.target.classList.contains("custom-modal-container")) {
    //             closeModal();
    //         }
    //     };
    //     document.addEventListener("click", handleBackdropClick);
    //     return () => {
    //         document.removeEventListener("click", handleBackdropClick);
    //     };
    // }, [isModalOpen]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const fuelArray = await getFuelRegistry(props.vesselMstId, fuelRegistryPayloadParsed);

                const transformedData = transformFuelData(props.vesselMstId, Array.isArray(fuelArray) ? fuelArray : []);
                setFuelData(transformedData);
            } catch (err) {
                console.error("Fetch error:", err);
            } finally {
                setIsLoading(false);
            }
        };


        fetchData();
    }, [props.vesselMstId, refreshTrigger]);

    const refreshTable = () => {
        setRefreshTrigger((prev) => prev + 1);
    };

    useEffect(() => {
        return () => {
            dispatch(myFleetActions.saveFuelRegistryPayload({ fuelRegistryPayload: null }));
        };
    }, [dispatch]);


    const fuelColumnsConfig = [
        { accessorKey: "FuelCode", header: "Fuel Type Id", enableEditing: false },
        { accessorKey: "FuelName", header: "Fuel Name", enableEditing: false },
        { accessorKey: "BDNPoS", header: "BDN ID", enableEditing: false },
        { accessorKey: "Quantity", header: "Quantity (MT)", enableEditing: false },
        { accessorKey: "EnergyContent", header: "Energy Content (MJ)", enableEditing: false },
        { accessorKey: "EValue", header: "E value (gCO2eq/MJ)", enableEditing: false },
        { accessorKey: "EUVALUE", header: "eᵤ (gCO2eq/MJ)", enableEditing: false },
        { accessorKey: "MassPercent", header: "% Mass", enableEditing: false },
        { accessorKey: "EnergyPercent", header: "% Energy", enableEditing: false },
        { accessorKey: "IMOLCV", header: "IMO LCV", enableEditing: false },
        { accessorKey: "EULCV", header: "EU LCV", enableEditing: false },
        { accessorKey: "CO2eqWtT", header: "CO2eq WtT", enableEditing: false },
        { accessorKey: "IMOCFCO2", header: "IMO CF CO2" },
        { accessorKey: "EUMRVEFCO2", header: "EUMRV EF CO2", enableEditing: false },
        { accessorKey: "EUMRVEFCH4", header: "EUMRV EF CH4", enableEditing: false },
        { accessorKey: "EUMRVEFN2O", header: "EUMRV EF N2O", enableEditing: false },
        { accessorKey: "EUETSEFCO2", header: "EUETS EF CO2", enableEditing: false },
        { accessorKey: "EUETSEFCH4", header: "EUETS EF CH4", enableEditing: false },
        { accessorKey: "EUETSEFN2O", header: "EUETS EF N2O", enableEditing: false },
        { accessorKey: "FEUEFCO2", header: "FEU EF CO2", enableEditing: false },
        { accessorKey: "FEUEFCH4", header: "FEU EF CH4", enableEditing: false },
        { accessorKey: "FEUEFN2O", header: "FEU EF N2O", enableEditing: false },
        { accessorKey: "UKMRVEFCO2", header: "UKMRV EF CO2" },
        { accessorKey: "RWD", header: "RWD", enableEditing: false },
        {
            accessorKey: "Cslip",
            header: "EU Cslip",
            editVariant: "select",
            enableEditing: false,
            editSelectOptions: [
                { label: "Applicable", value: "Applicable" },
                { label: "Not Applicable", value: "Not Applicable" },
            ],
        },
        { accessorKey: "UKETSEFCO2", header: "UKETS EF CO2", enableEditing: false },
        { accessorKey: "UKETSEFCH4", header: "UKETS EF CH4", enableEditing: false },
        { accessorKey: "UKETSEFN2O", header: "UKETS EF N2O", enableEditing: false },
        {
            accessorKey: "UKCslip",
            header: "UK Cslip",
            editVariant: "select",
            enableEditing: false,
            editSelectOptions: [
                { label: "Applicable", value: "Applicable" },
                { label: "Not Applicable", value: "Not Applicable" },
            ],
        },
    ];

    const renderBoldCell = (field) => ({ cell, row }) => {
        const value = cell.getValue();
        const isBold = row.original.MetaOverwriteMap?.[field];
        return <span style={{ fontWeight: isBold ? 900 : 400 }}>{value ?? ""}</span>;
    };

    const columns = useMemo(() => {
        const ICON_COLOR = "#042A4C";

        const fuelCategoryColumn = {
            accessorKey: "FuelCategory",
            header: "Fuel Category",
            size: 120,
            enableEditing: false,
            Cell: ({ cell, row }) => {
                const value = cell.getValue();
                const isBold = row.original.MetaOverwriteMap?.FuelCategory;
                return (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {value === "Fossil" && <LocalGasStationIcon sx={{ color: ICON_COLOR }} />}
                        {value === "Biofuel" && <SpaIcon sx={{ color: ICON_COLOR }} />}
                        {value === "Blend" && <ScienceIcon sx={{ color: ICON_COLOR }} />}
                        {value === "e-Fuel" && <BatteryChargingFullIcon sx={{ color: ICON_COLOR }} />}
                        <span
                            style={{
                                color:
                                    value === "Fossil"
                                        ? "#8B4513"
                                        : value === "Biofuel"
                                            ? "#2E7D32"
                                            : value === "Blend"
                                                ? "#6A1B9A"
                                                : value === "e-Fuel"
                                                    ? "#FFB300"
                                                    : "inherit",
                                fontWeight: isBold ? 700 : 600,
                            }}
                        >
                            {value}
                        </span>
                    </Box>
                );
            },
        };

        const mappedColumns = fuelColumnsConfig.map((col) => ({
            ...col,
            Cell: ({ cell, row }) => {
                // const isEFuel =
                //     row.original.FuelCategory?.toLowerCase() === "e-fuel";

                // Only control VALUE, not visibility
                // if (col.accessorKey === "EULCV") {
                //     return isEFuel
                //         ? (cell.getValue() ?? "")
                //         : "-"; // or "" if you prefer empty
                // }

                return renderBoldCell(col.accessorKey)({ cell, row });
            },
        }));

        return [fuelCategoryColumn, ...mappedColumns];
    }, []);


    const tableOptions = useMemo(() => ({
        columns,
        data: fuelData,
        enableExpanding: true,
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: true,
        initialState: {
            columnPinning: {
                left: [
                    "mrt-row-actions",
                    "mrt-row-expand",
                    "FuelCategory",
                    "FuelCode",
                    "FuelName",
                    
                ],
            },
        },
        state: {
            isLoading: isLoading,
        },
        enableTopToolbar: !props.isEnrollVessel && props.vesselMstId && (props?.regulations?.trim() === "EUMRV" || "UKMRV" || "DCS") ? false : true,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",

        enableEditing: (row) => row.original.IsEditable,
        enablePagination: false,
        displayColumnDefOptions: {
            "mrt-row-actions": {
                minSize: 100,
            },
        },
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },

        muiTableContainerProps: {
            sx: {
                maxHeight: "520px",
            },
        },

        muiTableBodyCellProps: ({ cell, row }) => {
            const hasOverwrite = row.original.MetaOverwriteMap
                ? Object.values(row.original.MetaOverwriteMap).some((val) => val === true)
                : false;

            return {
                sx: {
                    borderRight: "1px solid lightgray",
                    "&:last-child": { borderRight: "none" },
                    borderBottom: "1px solid lightgray",
                    backgroundColor: hasOverwrite
                        ? "rgba(255, 255, 0, 0.2)" // light yellow glow
                        : "inherit",
                    fontWeight: cell.column.id === "FuelCategory" ? "bold" : "normal",
                },
            };
        },
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
                backgroundColor: "#042a4c !important",

                "& .MuiTableCell-head::before": {
                    backgroundColor: "#042a4c !important",
                },
            },
        },
        getRowId: (row) => row._tempId,

        renderToolbarInternalActions: ({ table }) => <></>,
        renderRowActions: ({ row, table }) => {
            const editingRow = table.getState().editingRow;
            const isEditing = editingRow?.id === row.id;
            const canEdit = row.original.IsEditable;

            if (props.isReportTab) return null;

            // If the row is editable and we're not currently in the save/cancel mode
            if (canEdit && !isEditing) {
                return (
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => openEditModal(row)}
                        //onClick={() => table.setEditingRow(row)}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                );
            }

            // Render save/cancel buttons when the row is in editing state
            if (isEditing) {
                return (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Save">
                            <IconButton
                                size="small"
                                onClick={async () => {
                                    await table.options.onEditingRowSave?.({
                                        table,
                                        row,
                                        values: row.original,
                                    });
                                    table.setEditingRow(null);
                                }}
                            >
                                <SaveIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Cancel">
                            <IconButton color="error" size="small" onClick={() => table.setEditingRow(null)}>
                                <CancelIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            }

            return null;
        },
        onEditingRowSave: async ({ row, values, table }) => {
            // Implement  actual data update logic here
            table.setEditingRow(null);
        },

        renderTopToolbarCustomActions: ({ table }) => (
            <div className="mt-1">
                {props.vesselMstId ? (<Button
                    onClick={() => {
                        setSelectedFuel(null); // Clear for new fuel
                        openModal();
                    }}
                    startIcon={<Icon icon={ICONS.ADD} />}
                    disabled={!!table.getState().creatingRow}
                >
                    Add New Fuel
                </Button>) : ''}
            </div>
        ),
    }), [fuelData, isLoading, props.isEnrollVessel, props.vesselMstId, props.regulations, columns]);
    const table = useMaterialReactTable(tableOptions);
    return (
        <div className="fuel-registry mt-5">
            <MaterialReactTable table={table} />
            <AbsModal
                isOpen={isModalOpen}
                close={closeModal}
                //onClose={handleModalClose}
                title={selectedFuel ? "Update Fuel" : "Add New Fuel"}
                disableBackdropClick={true}
            >
                <FuelRegistryForm
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        refreshTable();
                    }}
                    vesselMstId={props.vesselMstId}
                    fuelRegId={selectedFuel?.FuelRegId || null}
                />
            </AbsModal>
        </div>
    );
};

export default BlendFuelRegistry;
